import React, { useMemo, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import {
  DataGrid,
  GridFooterContainer,
  useGridApiContext,
  useGridSelector,
  gridVisibleColumnDefinitionsSelector,
} from "@mui/x-data-grid";
import { CustomNoRowsOverlay } from "../../../../components/common/OverlayGrid";
import { commonDataGridSx } from "../../../../components/common/commonDataGridStyles";
import { useTheme } from "../../../../ThemeContext";

const ByProjectStickyFooter = ({ totals, hasData }) => {
  const { isDarkMode } = useTheme();
  const apiRef = useGridApiContext();
  const visibleColumns = useGridSelector(apiRef, gridVisibleColumnDefinitionsSelector);
  const scrollRef = useRef(null);

  useEffect(() => {
    const rootEl = apiRef.current?.rootElementRef?.current;
    if (!rootEl) return;
    const virtualScroller = rootEl.querySelector(".MuiDataGrid-virtualScroller");
    if (!virtualScroller) return;

    const handleScroll = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = virtualScroller.scrollLeft;
      }
    };

    virtualScroller.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      virtualScroller.removeEventListener("scroll", handleScroll);
    };
  }, [apiRef]);

  if (!hasData || !visibleColumns || visibleColumns.length === 0) return null;

  return (
    <GridFooterContainer
      sx={{
        backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
        borderTop: isDarkMode ? "1px solid #333" : "2px solid #e0e0e0",
        minHeight: "48px",
        height: "48px",
        p: 0,
        m: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          width: "100%",
          overflowX: "hidden",
          alignItems: "center",
          height: "48px",
        }}
      >
        {visibleColumns.map((col) => {
          let content = null;
          let color = isDarkMode ? "#f1f5f9" : "#1e293b";
          let fontWeight = 700;

          if (col.field === "project") {
            content = "Total";
          } else if (col.field === "model") {
            content = "—";
            color = isDarkMode ? "#94a3b8" : "#9ca3af";
          } else if (col.field === "hours") {
            content = totals?.hours || "0";
          } else if (col.field === "cost") {
            content = totals?.cost || "0.00";
          } else if (col.field === "earning") {
            content = totals?.earning || "0.00";
            color = isDarkMode ? "#4ade80" : "#16a34a";
          } else if (col.field === "status") {
            content = "";
          }

          return (
            <Box
              key={col.field}
              sx={{
                width: col.computedWidth,
                minWidth: col.computedWidth,
                maxWidth: col.computedWidth,
                px: "10px",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                fontSize: "13px",
                fontWeight,
                color,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {content}
            </Box>
          );
        })}
      </Box>
    </GridFooterContainer>
  );
};

const ByProjectBreakDown = ({
  selectedUserId,
  inProgress = [],
  byProject = [],
  isLoading = false,
  isDarkMode = false,
}) => {
  const byProjectColumns = useMemo(
    () => [
      {
        field: "project",
        headerName: "Project",
        flex: 1.8,
        minWidth: 180,
        colSpan: (value, row) => (row?.isDivider ? 6 : undefined),
        renderCell: (params) => {
          if (params.row.isDivider) {
            return (
              <span style={{ fontSize: "12px", color: "#6b7280", fontStyle: "italic" }}>
                {params.row.project}
              </span>
            );
          }
          return (
            <span
              style={{
                fontWeight: params.row.isTotal ? 700 : 500,
                color: params.row.isTotal ? "#1e293b" : "#374151",
                fontSize: "13px",
              }}
            >
              {params.value}
            </span>
          );
        },
      },
      {
        field: "model",
        headerName: "Model",
        flex: 0.8,
        minWidth: 90,
        renderCell: (params) => {
          if (params.row.isDivider) return null;
          if (params.row.isTotal) {
            return <span style={{ color: "#9ca3af", fontSize: "13px", fontWeight: 700 }}>—</span>;
          }
          const modelType = params.row.budget_type || params.value;
          if (modelType === "FIXED_COST" || modelType === "Fixed" || modelType === "Fixed Cost") {
            return <span className="badge-model-fixed">Fixed Cost</span>;
          }
          if (modelType === "TIME_AND_MATERIAL" || modelType === "T&M(Contract)") {
            return <span className="badge-model-contract">T&M(Contract)</span>;
          }
          if (modelType === "CONTRACT" || modelType === "Contract") {
            return <span className="badge-model-contract">Contract</span>;
          }
          return <span style={{ color: "#9ca3af", fontSize: "13px" }}>{params.value}</span>;
        },
      },
      {
        field: "hours",
        headerName: "Hours",
        flex: 0.8,
        minWidth: 90,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => {
          if (params.row.isDivider) return null;
          return (
            <span
              style={{
                fontWeight: params.row.isTotal ? 700 : 400,
                color: params.row.id === 3 ? "#6b7280" : "#374151",
                fontSize: "13px",
              }}
            >
              {params.value}
            </span>
          );
        },
      },
      {
        field: "cost",
        headerName: "Cost",
        flex: 1.1,
        minWidth: 120,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => {
          if (params.row.isDivider) return null;
          return (
            <span
              style={{
                fontWeight: params.row.isTotal ? 700 : 400,
                color: params.row.costType === "danger" ? "#dc2626" : "#374151",
                fontSize: "13px",
              }}
            >
              {params.value}
            </span>
          );
        },
      },
      {
        field: "earning",
        headerName: "Earning",
        flex: 1.1,
        minWidth: 120,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => {
          if (params.row.isDivider) return null;
          return (
            <span
              style={{
                fontWeight: params.row.isTotal ? 700 : 400,
                color: params.row.isTotal ? "#16a34a" : params.value === "—" ? "#9ca3af" : "#6b7280",
                fontSize: "13px",
              }}
            >
              {params.value}
            </span>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1.3,
        minWidth: 150,
        renderCell: (params) => {
          if (params.row.isDivider || params.row.isTotal) return null;
          if (params.value === "Pending completion" || params.value === "Mapped") {
            return (
              <span className="badge-status-mapped">
                {params.value}
              </span>
            );
          }
          if (params.value === "Allocated") {
            return (
              <span className="badge-status-allocated">
                {params.value}
              </span>
            );
          }
          if (params.value === "Unmapped") {
            return (
              <span className="badge-status-unmapped">
                {params.value}
              </span>
            );
          }
          if (params.value === "Provisional") {
            return (
              <span className="badge-status-provisional">
                {params.value}
              </span>
            );
          }
          if (params.value === "Too early") {
            return (
              <span className="badge-status-too-early">
                {params.value}
              </span>
            );
          }
          return (
            <span style={{ color: "#6b7280", fontSize: "13px" }}>
              {params.value}
            </span>
          );
        },
      },
    ],
    []
  );

  const byProjectTotals = useMemo(() => {
    let totalHours = 0;
    let totalCost = 0;
    let totalEarning = 0;

    if (Array.isArray(inProgress)) {
      inProgress.forEach((p) => {
        totalHours += Number(p.working_hours || 0);
        totalCost += Number(p.cost || 0);
        totalEarning += Number(p.provisional_earning || p.received_amount || 0);
      });
    }

    if (Array.isArray(byProject)) {
      byProject.forEach((p) => {
        totalHours += Number(p.total_hours || 0);
        totalCost += Number(p.total_cost || 0);
        totalEarning += Number(p.earning || 0);
      });
    }

    return {
      hours: totalHours % 1 === 0 ? String(totalHours) : totalHours.toFixed(2),
      cost: totalCost.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      earning: totalEarning.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    };
  }, [inProgress, byProject]);

  const byProjectRows = useMemo(() => {
    if (!selectedUserId) return [];
    let rows = [];

    // 1. In Progress projects (Fixed Cost provisional)
    if (Array.isArray(inProgress) && inProgress.length > 0) {
      inProgress.forEach((item, idx) => {
        const modelFmt =
          item.model === "CONTRACT"
            ? "Contract"
            : item.model === "FIXED_COST"
            ? "Fixed Cost"
            : item.model === "TIME_AND_MATERIAL"
            ? "T&M(Contract)"
            : item.model || "Fixed Cost";

        rows.push({
          id: `prog-${item.project_id || idx}`,
          project: item.project || "—",
          model: modelFmt,
          budget_type: item.budget_type || item.model,
          hours: Number(item.working_hours || 0).toFixed(2),
          cost: Number(item.cost || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          earning: Number(item.provisional_earning || item.earning || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          status: item.status || "Provisional",
          isDivider: false,
          isTotal: false,
        });
      });
    }

    // 2. Completed / By Project breakdown
    if (Array.isArray(byProject) && byProject.length > 0) {
      byProject.forEach((item, idx) => {
        const modelFmt =
          item.model === "CONTRACT"
            ? "Contract"
            : item.model === "FIXED_COST"
            ? "Fixed Cost"
            : item.model === "TIME_AND_MATERIAL"
            ? "T&M(Contract)"
            : item.model || "Contract";

        rows.push({
          id: `proj-${idx}`,
          project: item.project || "—",
          model: modelFmt,
          budget_type: item.budget_type || item.model,
          hours: Number(item.total_hours || 0).toFixed(2),
          cost: Number(item.total_cost || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          earning: Number(item.earning || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          status: item.status || "Pending completion",
          isDivider: false,
          isTotal: false,
        });
      });
    }

    return rows;
  }, [inProgress, byProject, selectedUserId]);

  const isByProjectScrollable = byProjectRows.length > 10;

  const ByProjectNoRowsOverlay = () => {
    if (!selectedUserId) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            fontSize: "16px",
            fontWeight: "bold",
            backgroundColor: isDarkMode ? "#272626" : "#f0f0f0",
            color: isDarkMode ? "white" : "#888",
          }}
        >
          Select resource and view project details
        </Box>
      );
    }
    return <CustomNoRowsOverlay />;
  };

  return (
    <div className={`card border rounded shadow-sm mb-4 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
      <div className={`card-header ${isDarkMode ? "bg-dark text-white border-secondary" : "bg-white"} py-3 border-bottom d-flex align-items-center justify-content-between`}>
        <div className="d-flex align-items-center gap-2">
          <span
            className="rounded-circle"
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "rgb(4, 161, 137)",
            }}
          ></span>
          <span className="fw-semibold" style={{ fontSize: "14px" }}>
            By project breakdown
          </span>
          {selectedUserId && (
            <span className="text-muted small ms-2">
              {byProjectRows.length > 0
                ? `${byProjectRows.length} project allocations`
                : "No projects"}
            </span>
          )}
        </div>
      </div>

      <Box
        sx={{
          width: "100%",
          backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
          height: isByProjectScrollable ? 584 : "auto",
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={isLoading ? [] : byProjectRows}
          columns={byProjectColumns}
          loading={isLoading}
          autoHeight={!isByProjectScrollable}
          hideFooterPagination
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          rowHeight={48}
          getRowClassName={(params) =>
            params.row.isDivider ? "by-project-divider-row" : ""
          }
          slots={{
            footer: ByProjectStickyFooter,
            noRowsOverlay: ByProjectNoRowsOverlay,
          }}
          slotProps={{
            footer: {
              totals: byProjectTotals,
              hasData: Boolean(
                selectedUserId &&
                  !isLoading &&
                  (inProgress.length > 0 || byProject.length > 0)
              ),
            },
          }}
          sx={{
            ...commonDataGridSx,
            "& .by-project-divider-row": {
              backgroundColor: "#f9fafb !important",
              minHeight: "36px !important",
              "&:hover": {
                backgroundColor: "#f9fafb !important",
              },
            },
          }}
        />
      </Box>
    </div>
  );
};

export default ByProjectBreakDown;
